'use client';
import Link from 'next/link';
import { useRef } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion.jsx';
import { NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, } from '../../ui/navigation-menu.jsx';
import { cn } from '../../../lib/utils.js';
import { SubMenuLink } from './sub-menu-link';
var menuItemClassName = 'hover:bg-accent hover:border-foreground hover:text-accent-foreground text-foreground group inline-flex h-14 w-max items-center justify-center rounded-none border-b-2 border-transparent px-3 pb-3.5 pt-4 text-sm font-medium transition-colors';
/**
 * A parent entry carries the hover and border treatment on its `li` rather than on either of its
 * two controls, so the link and the chevron toggle still read as one highlighted entry. Identical
 * to {@link menuItemClassName} except that the padding moves to the two controls and they stretch
 * to fill the entry, which keeps each one's click target the full height of the navbar.
 */
var parentItemClassName = 'hover:bg-accent hover:border-foreground hover:text-accent-foreground text-foreground inline-flex h-14 w-max items-stretch justify-center rounded-none border-b-2 border-transparent text-sm font-medium transition-colors';
/** The vertical padding {@link parentItemClassName} gives up, so the label keeps its baseline. */
var parentControlClassName = 'flex items-center pt-4 pb-3.5';
var focusRingClassName = 'outline-none focus-visible:ring-2 focus-visible:ring-ring/70';
export var MenuItem = function (_a) {
    var item = _a.item, className = _a.className, onHover = _a.onHover;
    var entryRef = useRef(null);
    var pointerTypeRef = useRef('');
    if (item.items) {
        var itemValue_1 = item.key || item.title;
        // Radix scopes hover open and close to the trigger, which here is only the chevron. The entry
        // and its dropdown report hover upward instead so that `DesktopMenu` can treat them as one
        // region; Radix's own close scheduling is suppressed below so the two do not fight.
        var handleHover_1 = function (event, hoveredValue) {
            if (event.pointerType !== 'mouse')
                return;
            onHover === null || onHover === void 0 ? void 0 : onHover(hoveredValue);
        };
        // Radix closes on any pointer-leave of the trigger or the content, including a move onto the
        // entry's own label. `composeEventHandlers` runs a passed handler first and skips Radix's once
        // the event is default prevented, which is how the trigger already opts out of hover in
        // `click` mode. The same lever suppresses the rest of Radix's pointer-driven state below.
        var suppressRadixHoverClose_1 = function (event) { return event.preventDefault(); };
        return (<NavigationMenuItem ref={entryRef} value={itemValue_1} className={cn(parentItemClassName, className)} onPointerEnter={function (event) { return handleHover_1(event, itemValue_1); }} onPointerLeave={function (event) { return handleHover_1(event, null); }}>
        {/*
              The label and the chevron are separate controls on purpose. Radix renders the trigger as
              a `button`, so making the label itself the trigger would either nest an `a` inside that
              `button` — invalid HTML that React reports as a hydration error, and a route change that
              races the sub-item the user is reaching for — or leave the sub-menu unreachable by
              keyboard, since Radix's arrow-key handler only enters a menu that is already open.
            */}
        {/*
              Following the label dismisses the menu by default, which would shut it while the pointer
              is still resting on the entry. Hover is the only thing that should open or close it, so
              the entry stays open until the pointer leaves. Sub-item links keep the default dismissal —
              there the menu has served its purpose and would otherwise cover the page just opened.
            */}
        <NavigationMenuLink asChild onSelect={function (event) { return event.preventDefault(); }}>
          <Link className={cn(parentControlClassName, 'pr-0 pl-3', focusRingClassName)} href={item.url}>
            {item.title}
          </Link>
        </NavigationMenuLink>
        <NavigationMenuTrigger aria-label={"Show ".concat(item.title, " submenu")} onPointerLeave={suppressRadixHoverClose_1} onPointerDown={function (event) {
                pointerTypeRef.current = event.pointerType;
            }} 
        // Hover alone drives this menu, so a mouse click must not toggle it: clicking the entry
        // to follow its link would otherwise shut the menu the pointer is still sitting inside.
        // The two inputs with no hover to fall back on keep toggling — keyboard activation,
        // which reports `detail === 0`, and touch, which Radix excludes from hover entirely.
        onClick={function (event) {
                if (event.detail > 0 && pointerTypeRef.current === 'mouse')
                    event.preventDefault();
            }} className={cn(parentControlClassName, 'h-auto rounded-none bg-transparent pr-3 pl-0 hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent', focusRingClassName)}/>
        <NavigationMenuContent onPointerEnter={function (event) { return handleHover_1(event, itemValue_1); }} onPointerLeave={function (event) {
                suppressRadixHoverClose_1(event);
                handleHover_1(event, null);
            }} onPointerDownOutside={function (event) {
                var _a;
                // Radix exempts the trigger from dismiss-on-outside-press, but the trigger is only the
                // chevron, so pressing the label beside it dismisses the menu before the click even
                // lands. Exempt the whole entry, matching what Radix does for an unsplit trigger.
                if ((_a = entryRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))
                    event.preventDefault();
            }}>
          <ul className="w-110 p-3">
            {item.items.map(function (subItem) { return (<li key={subItem.url}>
                <SubMenuLink className={cn('hover:bg-accent hover:text-accent-foreground flex gap-4 rounded-md p-3 leading-none no-underline transition-colors select-none', focusRingClassName)} href={subItem.url}>
                  {subItem.icon && <div className="shrink-0">{subItem.icon}</div>}
                  <div>
                    <div className="text-foreground text-sm font-semibold">{subItem.title}</div>
                    {subItem.description && (<p className="text-muted-foreground text-sm leading-snug">
                        {subItem.description}
                      </p>)}
                  </div>
                </SubMenuLink>
              </li>); })}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>);
    }
    return (<NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link className={cn(menuItemClassName, className)} href={item.url}>
          {!!item.icon && <div className="mr-1 shrink-0">{item.icon}</div>}
          {item.title}
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>);
};
export var MobileMenuItem = function (_a) {
    var item = _a.item;
    if (item.items) {
        return (<AccordionItem value={item.title} className="border-b-0">
        <AccordionTrigger className="text-foreground text-md py-0 font-semibold hover:no-underline">
          {item.title}
        </AccordionTrigger>
        <AccordionContent className="mt-2">
          {item.items.map(function (subItem) { return (<Link key={subItem.url} className="hover:bg-accent hover:text-accent-foreground flex gap-4 rounded-md p-3 leading-none transition-colors outline-none select-none" href={subItem.url}>
              {subItem.icon && <div className="shrink-0">{subItem.icon}</div>}
              <div>
                <div className="text-foreground text-sm font-semibold">{subItem.title}</div>
                {subItem.description && (<p className="text-muted-foreground text-sm leading-snug">
                    {subItem.description}
                  </p>)}
              </div>
            </Link>); })}
        </AccordionContent>
      </AccordionItem>);
    }
    return (<Link href={item.url} className="text-foreground font-semibold">
      {item.title}
    </Link>);
};
